import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';

function QuickNotesDropdown() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingText, setSavingText] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');
  const [viewingNote, setViewingNote] = useState(null);
  const fileInputRef = useRef(null);

  const getErrorMessage = (err, fallback = 'Something went wrong.') => {
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      fallback
    );
  };

  const normalizeNotes = (data) => {
    const rawNotes = Array.isArray(data)
      ? data
      : Array.isArray(data?.notes)
        ? data.notes
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.rows)
            ? data.rows
            : Array.isArray(data?.results)
              ? data.results
              : Array.isArray(data?.items)
                ? data.items
                : data?.note
                  ? [data.note]
                  : [];

    return rawNotes.map((note, index) => ({
      ...note,
      id: note.id || note.note_id || note.quick_note_id || `quick-note-${index}`,
      user_id: note.user_id || note.userId || null,
      type:
        note.type ||
        note.note_type ||
        (note.file_url || note.fileUrl || note.url ? 'file' : 'text'),
      content: note.content || note.note || note.text || note.body || '',
      file_url: note.file_url || note.fileUrl || note.url || '',
      original_name:
        note.original_name ||
        note.originalName ||
        note.file_name ||
        note.filename ||
        note.name ||
        '',
      is_pinned: Number(note.is_pinned || note.isPinned || 0),
      created_at:
        note.created_at ||
        note.createdAt ||
        note.created ||
        note.updated_at ||
        new Date().toISOString(),
      updated_at: note.updated_at || note.updatedAt || null,
    }));
  };

  const getNoteType = (note) => {
    return String(note?.type || '').toLowerCase().trim();
  };

  const isTextNote = (note) => {
    const type = getNoteType(note);

    return (
      type === 'text' ||
      (!note?.file_url && !note?.fileUrl && typeof note?.content === 'string')
    );
  };

  const isImageNote = (note) => {
    const type = getNoteType(note);

    if (type === 'image') return true;

    const fileUrl = String(note?.file_url || note?.fileUrl || '').toLowerCase();
    return /\.(jpg|jpeg|png|webp|gif)$/i.test(fileUrl);
  };

  const isFileNote = (note) => {
    const type = getNoteType(note);
    return type === 'file' || (!!note?.file_url && !isImageNote(note));
  };

  const getDisplayType = (note) => {
    if (isImageNote(note)) return 'Image';
    if (isFileNote(note)) return 'File';
    return 'Text';
  };

  const normalizeFileUrl = (url) => {
    if (!url) return '';

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }

    return `${window.location.origin}/${url}`;
  };

  const getDownloadName = (note) => {
    const fallback = isImageNote(note) ? 'quick-note-image' : 'quick-note-file';

    const name =
      note?.original_name ||
      note?.originalName ||
      note?.file_name ||
      note?.filename ||
      note?.name ||
      fallback;

    return String(name).trim() || fallback;
  };

  const sortedNotes = useMemo(() => {
    return [...notes].sort((a, b) => {
      if (Number(b.is_pinned) !== Number(a.is_pinned)) {
        return Number(b.is_pinned) - Number(a.is_pinned);
      }

      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [notes]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await api.get(`/quick-notes?_=${Date.now()}`, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      const payload = res?.data || res;
      const loadedNotes = normalizeNotes(payload);

      console.log('[QuickNotes] PAYLOAD:', payload);
      console.log('[QuickNotes] Loaded notes count:', loadedNotes.length);
      console.log('[QuickNotes] Loaded notes:', loadedNotes);

      setNotes(loadedNotes);
    } catch (err) {
      console.error('Failed to load quick notes:', err);
      setError(getErrorMessage(err, 'Could not load notes.'));
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  useEffect(() => {
    if (open) {
      loadNotes();
    }
  }, [open]);

  const saveTextNote = async () => {
    const clean = content.trim();

    if (!clean || savingText) return;

    try {
      setSavingText(true);
      setError('');

      const res = await api.post('/quick-notes', {
        content: clean,
        type: 'text',
      });

      const savedNote = normalizeNotes(res?.data || res)?.[0];

      if (savedNote) {
        setNotes((prev) => [savedNote, ...prev]);
      }

      setContent('');
      await loadNotes();
    } catch (err) {
      console.error('Failed to save quick note:', err);
      setError(getErrorMessage(err, 'Could not save note.'));
    } finally {
      setSavingText(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file || uploading) return;

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/quick-notes/upload', formData);

      const uploadedNote = normalizeNotes(res?.data || res)?.[0];

      if (uploadedNote) {
        setNotes((prev) => [uploadedNote, ...prev]);
      }

      await loadNotes();
    } catch (err) {
      console.error('Failed to upload quick note file:', err);
      setError(getErrorMessage(err, 'Could not upload file.'));
    } finally {
      setUploading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadNoteFile = async (note) => {
    if (!note?.id || downloadingId) return;

    try {
      setDownloadingId(note.id);
      setError('');

      const response = await api.get(`/quick-notes/${note.id}/download`, {
        responseType: 'blob',
      });

      const contentType =
        response?.headers?.['content-type'] ||
        response?.headers?.get?.('content-type') ||
        'application/octet-stream';

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = getDownloadName(note);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
      setError(getErrorMessage(err, 'Download failed.'));
    } finally {
      setDownloadingId(null);
    }
  };

  const deleteNote = async (noteId) => {
    if (!noteId) return;

    try {
      setError('');
      setNotes((prev) => prev.filter((note) => note.id !== noteId));

      await api.delete(`/quick-notes/${noteId}`);
      await loadNotes();

      if (viewingNote?.id === noteId) {
        setViewingNote(null);
      }
    } catch (err) {
      console.error('Failed to delete quick note:', err);
      setError(getErrorMessage(err, 'Could not delete note.'));
      await loadNotes();
    }
  };

  const togglePin = async (note) => {
    if (!note?.id) return;

    const nextPinned = note.is_pinned ? 0 : 1;

    try {
      setError('');

      setNotes((prev) =>
        prev.map((item) =>
          item.id === note.id ? { ...item, is_pinned: nextPinned } : item
        )
      );

      await api.patch(`/quick-notes/${note.id}/pin`, {
        is_pinned: nextPinned,
      });

      await loadNotes();
    } catch (err) {
      console.error('Failed to pin quick note:', err);
      setError(getErrorMessage(err, 'Could not update pin.'));
      await loadNotes();
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setError('');
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      setError('Copy failed.');
    }
  };

  const copyLink = async (url) => {
    const fullUrl = normalizeFileUrl(url);

    if (!fullUrl) {
      setError('No file link found for this note.');
      return;
    }

    await copyText(fullUrl);
  };

  const handlePaste = async (event) => {
    const items = event.clipboardData?.items || [];

    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();

        if (file) {
          event.preventDefault();
          await uploadFile(file);
          return;
        }
      }
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      setOpen(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={styles.floatingButton}
        title="Quick Notes"
      >
        📝 Notes
        {notes.length > 0 && <span style={styles.badge}>{notes.length}</span>}
      </button>

      {open && (
        <div style={styles.overlay} onMouseDown={handleOverlayClick}>
          <div style={styles.panel} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.header}>
              <div>
                <h3 style={styles.title}>Quick Notes</h3>
                <p style={styles.subtitle}>
                  Send text, images, and files between computers.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                style={styles.closeButton}
              >
                ✕
              </button>
            </div>

            <div style={styles.inputBox}>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onPaste={handlePaste}
                placeholder="Type or paste something here..."
                style={styles.textarea}
                rows={4}
              />

              <div style={styles.actionsRow}>
                <button
                  type="button"
                  onClick={saveTextNote}
                  disabled={savingText || !content.trim()}
                  style={{
                    ...styles.primaryButton,
                    opacity: savingText || !content.trim() ? 0.6 : 1,
                    cursor: savingText || !content.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {savingText ? 'Saving...' : 'Save Note'}
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    ...styles.secondaryButton,
                    opacity: uploading ? 0.6 : 1,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {uploading ? 'Uploading...' : 'Upload Image/File'}
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.txt,.csv,.xlsx,.xls,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={(e) => uploadFile(e.target.files?.[0])}
                />
              </div>

              <div style={styles.hint}>
                Tip: You can paste a screenshot directly into this box.
              </div>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.notesHeader}>
              <div>
                <strong>Saved Notes</strong>
                <div style={styles.debugText}>Notes loaded: {notes.length}</div>
              </div>

              <button
                type="button"
                onClick={loadNotes}
                disabled={loading}
                style={{
                  ...styles.refreshButton,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            <div style={styles.notesList}>
              {loading && sortedNotes.length === 0 ? (
                <div style={styles.empty}>Loading notes...</div>
              ) : sortedNotes.length === 0 ? (
                <div style={styles.empty}>No notes yet.</div>
              ) : (
                sortedNotes.map((note) => {
                  const fileUrl = normalizeFileUrl(note.file_url);
                  const isDownloading = downloadingId === note.id;

                  return (
                    <div key={note.id} style={styles.noteCard}>
                      <div style={styles.noteTop}>
                        <span style={styles.noteType}>
                          {note.is_pinned ? '📌 ' : ''}
                          {getDisplayType(note)}
                        </span>

                        <span style={styles.dateText}>
                          {note.created_at
                            ? new Date(note.created_at).toLocaleString()
                            : ''}
                        </span>
                      </div>

                      {isTextNote(note) && (
                        <div style={styles.noteText}>
                          {note.content || 'No text saved.'}
                        </div>
                      )}

                      {isImageNote(note) && note.file_url && (
                        <div>
                          <button
                            type="button"
                            onClick={() => downloadNoteFile(note)}
                            disabled={isDownloading}
                            style={{
                              ...styles.imageDownloadButton,
                              opacity: isDownloading ? 0.65 : 1,
                              cursor: isDownloading ? 'wait' : 'pointer',
                            }}
                            title="Click to download image"
                          >
                            <img
                              src={fileUrl}
                              alt={note.original_name || 'Quick note upload'}
                              style={styles.imagePreview}
                            />
                          </button>

                          <div style={styles.fileName}>
                            {note.original_name || 'Image'}
                          </div>

                          <div style={styles.imageHint}>
                            {isDownloading ? 'Downloading...' : 'Click image to download'}
                          </div>
                        </div>
                      )}

                      {isFileNote(note) && note.file_url && !isImageNote(note) && (
                        <div style={styles.fileBox}>
                          <div style={styles.fileIcon}>📎</div>
                          <div>
                            <div style={styles.fileName}>
                              {note.original_name || 'File'}
                            </div>
                            <button
                              type="button"
                              onClick={() => downloadNoteFile(note)}
                              disabled={isDownloading}
                              style={styles.fileDownloadButton}
                            >
                              {isDownloading ? 'Downloading...' : 'Download file'}
                            </button>
                          </div>
                        </div>
                      )}

                      <div style={styles.noteActions}>
                        {isTextNote(note) ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setViewingNote(note)}
                              style={styles.smallButton}
                            >
                              Open
                            </button>

                            <button
                              type="button"
                              onClick={() => copyText(note.content)}
                              style={styles.smallButton}
                            >
                              Copy Text
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => copyLink(note.file_url)}
                              style={styles.smallButton}
                            >
                              Copy Link
                            </button>

                            <button
                              type="button"
                              onClick={() => downloadNoteFile(note)}
                              disabled={isDownloading}
                              style={{
                                ...styles.smallButton,
                                opacity: isDownloading ? 0.65 : 1,
                                cursor: isDownloading ? 'wait' : 'pointer',
                              }}
                            >
                              {isDownloading ? 'Downloading...' : 'Download'}
                            </button>
                          </>
                        )}

                        <button
                          type="button"
                          onClick={() => togglePin(note)}
                          style={styles.smallButton}
                        >
                          {note.is_pinned ? 'Unpin' : 'Pin'}
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteNote(note.id)}
                          style={styles.dangerButton}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {viewingNote && (
        <div style={styles.viewOverlay} onMouseDown={() => setViewingNote(null)}>
          <div style={styles.viewModal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.viewHeader}>
              <div>
                <h3 style={styles.viewTitle}>Text Note</h3>
                <p style={styles.viewDate}>
                  {viewingNote.created_at
                    ? new Date(viewingNote.created_at).toLocaleString()
                    : ''}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setViewingNote(null)}
                style={styles.closeButton}
              >
                ✕
              </button>
            </div>

            <div style={styles.viewContent}>
              {viewingNote.content || 'No text saved.'}
            </div>

            <div style={styles.noteActions}>
              <button
                type="button"
                onClick={() => copyText(viewingNote.content)}
                style={styles.primaryButton}
              >
                Copy Text
              </button>

              <button
                type="button"
                onClick={() => setViewingNote(null)}
                style={styles.secondaryButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  floatingButton: {
    border: '1px solid rgba(255, 255, 255, 0.55)',
    background: 'transparent',
    color: '#f8fafc',
    padding: '6px 12px',
    borderRadius: '6px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '38px',
    marginLeft: '8px',
    position: 'relative',
  },
  badge: {
    marginLeft: '4px',
    background: '#2563eb',
    color: '#fff',
    borderRadius: '999px',
    padding: '2px 7px',
    fontSize: '12px',
    lineHeight: 1.2,
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(2, 6, 23, 0.55)',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  viewOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'rgba(2, 6, 23, 0.72)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '18px',
  },
  panel: {
    width: 'min(520px, 100vw)',
    height: '100vh',
    background: '#0b1120',
    color: '#e5e7eb',
    boxShadow: '-24px 0 60px rgba(0,0,0,0.45)',
    padding: '18px',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: '1px solid rgba(148, 163, 184, 0.18)',
  },
  viewModal: {
    width: 'min(680px, 100%)',
    maxHeight: '85vh',
    overflowY: 'auto',
    background: '#0b1120',
    color: '#e5e7eb',
    border: '1px solid rgba(148, 163, 184, 0.22)',
    borderRadius: '18px',
    boxShadow: '0 24px 80px rgba(0,0,0,0.55)',
    padding: '18px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '14px',
  },
  viewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '14px',
  },
  title: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 900,
  },
  viewTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 900,
  },
  subtitle: {
    margin: '5px 0 0',
    color: '#94a3b8',
    fontSize: '13px',
  },
  viewDate: {
    margin: '5px 0 0',
    color: '#94a3b8',
    fontSize: '12px',
  },
  closeButton: {
    border: '1px solid rgba(148, 163, 184, 0.25)',
    background: '#111827',
    color: '#f8fafc',
    borderRadius: '10px',
    width: '36px',
    height: '36px',
    cursor: 'pointer',
  },
  inputBox: {
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    borderRadius: '16px',
    padding: '12px',
    marginBottom: '12px',
  },
  textarea: {
    width: '100%',
    resize: 'vertical',
    minHeight: '96px',
    borderRadius: '12px',
    border: '1px solid rgba(148, 163, 184, 0.22)',
    background: '#020617',
    color: '#f8fafc',
    padding: '12px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  actionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '10px',
  },
  primaryButton: {
    border: 0,
    background: '#2563eb',
    color: '#fff',
    borderRadius: '10px',
    padding: '9px 12px',
    fontWeight: 800,
    cursor: 'pointer',
  },
  secondaryButton: {
    border: '1px solid rgba(148, 163, 184, 0.25)',
    background: '#1f2937',
    color: '#f8fafc',
    borderRadius: '10px',
    padding: '9px 12px',
    fontWeight: 800,
    cursor: 'pointer',
  },
  hint: {
    color: '#94a3b8',
    fontSize: '12px',
    marginTop: '8px',
  },
  error: {
    background: 'rgba(239, 68, 68, 0.12)',
    border: '1px solid rgba(239, 68, 68, 0.35)',
    color: '#fecaca',
    padding: '10px 12px',
    borderRadius: '12px',
    marginBottom: '12px',
    wordBreak: 'break-word',
  },
  notesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '4px 0 10px',
  },
  debugText: {
    color: '#93c5fd',
    fontSize: '12px',
    marginTop: '3px',
  },
  refreshButton: {
    border: '1px solid rgba(148, 163, 184, 0.25)',
    background: '#111827',
    color: '#f8fafc',
    borderRadius: '10px',
    padding: '7px 10px',
  },
  notesList: {
    overflowY: 'auto',
    paddingRight: '4px',
    flex: 1,
  },
  empty: {
    color: '#94a3b8',
    textAlign: 'center',
    padding: '30px 10px',
  },
  noteCard: {
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.16)',
    borderRadius: '16px',
    padding: '12px',
    marginBottom: '10px',
  },
  noteTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '8px',
  },
  noteType: {
    fontWeight: 900,
    color: '#bfdbfe',
  },
  dateText: {
    color: '#64748b',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  noteText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#f8fafc',
    background: '#020617',
    borderRadius: '12px',
    padding: '10px',
    border: '1px solid rgba(148, 163, 184, 0.12)',
    maxHeight: '120px',
    overflow: 'hidden',
    marginTop: '8px',
  },
  viewContent: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    background: '#020617',
    border: '1px solid rgba(148, 163, 184, 0.16)',
    borderRadius: '14px',
    padding: '14px',
    color: '#f8fafc',
    fontSize: '15px',
    lineHeight: 1.5,
  },
  imageDownloadButton: {
    display: 'block',
    cursor: 'pointer',
    border: 0,
    padding: 0,
    margin: 0,
    width: '100%',
    background: 'transparent',
  },
  imageHint: {
    color: '#93c5fd',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 700,
  },
  imagePreview: {
    width: '100%',
    maxHeight: '240px',
    objectFit: 'contain',
    background: '#020617',
    borderRadius: '12px',
    border: '1px solid rgba(148, 163, 184, 0.12)',
  },
  fileBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#020617',
    border: '1px solid rgba(148, 163, 184, 0.12)',
    borderRadius: '12px',
    padding: '12px',
  },
  fileIcon: {
    fontSize: '26px',
  },
  fileName: {
    color: '#cbd5e1',
    fontSize: '13px',
    marginTop: '6px',
    wordBreak: 'break-word',
  },
  fileDownloadButton: {
    border: 0,
    background: 'transparent',
    color: '#93c5fd',
    fontWeight: 800,
    fontSize: '13px',
    padding: 0,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  noteActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '10px',
  },
  smallButton: {
    border: '1px solid rgba(148, 163, 184, 0.22)',
    background: '#1f2937',
    color: '#f8fafc',
    borderRadius: '10px',
    padding: '7px 10px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  dangerButton: {
    border: '1px solid rgba(239, 68, 68, 0.35)',
    background: 'rgba(239, 68, 68, 0.12)',
    color: '#fecaca',
    borderRadius: '10px',
    padding: '7px 10px',
    cursor: 'pointer',
    fontWeight: 800,
  },
};

export default QuickNotesDropdown;